import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Building2, MapPin, Edit, Trash2, Loader2, X, Users, Globe, Search, RotateCcw, RefreshCw, Upload, Download, Link, Sparkles, CheckCircle, FileText, ExternalLink } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Textarea } from '../../components/ui/textarea';
import AdvancedDataGrid from '../../components/AdvancedDataGrid';
import CSVImportWizard from '../../components/CSVImportWizard';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const COUNTRIES = ['USA', 'Canada', 'UK', 'India', 'Australia', 'Germany', 'France', 'Other'];
const ATS_OPTIONS = ['JobDiva', 'BullHorn', 'Salesforce', 'Greenhouse', 'Workday', 'iCIMS', 'Taleo'];
const AI_VOICE_OPTIONS = ['JobTalk', 'ConverzAI', 'HeyMilo', 'Glider', 'Paradox', 'HireVue'];

// Defined OUTSIDE CompaniesPage so it doesn't remount on every parent render (which kills input focus)
function LocationForm({ locations, onAdd, onUpdate, onRemove }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base font-semibold">Locations</Label>
        <Button type="button" variant="outline" size="sm" onClick={onAdd} data-testid="add-location-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Location
        </Button>
      </div>
      {locations.length === 0 ? (
        <div className="text-center py-6 border border-dashed rounded-lg">
          <MapPin className="h-8 w-8 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No locations yet. Add one above.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {locations.map((loc, index) => (
            <div key={loc.id || index} className="border rounded-lg p-4 space-y-3 bg-slate-50">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-700">Location {index + 1}</span>
                <Button type="button" variant="ghost" size="sm" onClick={() => onRemove(index)} className="text-red-600 hover:text-red-700">
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Location Name</Label>
                  <Input value={loc.name} onChange={(e) => onUpdate(index, 'name', e.target.value)} placeholder="e.g., Headquarters" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Phone</Label>
                  <Input value={loc.phone} onChange={(e) => onUpdate(index, 'phone', e.target.value)} placeholder="+1 (555) 123-4567" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Address</Label>
                <Input value={loc.address} onChange={(e) => onUpdate(index, 'address', e.target.value)} placeholder="123 Main Street" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">City</Label>
                  <Input value={loc.city} onChange={(e) => onUpdate(index, 'city', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">State</Label>
                  <Input value={loc.state} onChange={(e) => onUpdate(index, 'state', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Country</Label>
                  <Select value={loc.country} onValueChange={(v) => onUpdate(index, 'country', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function OwnerBadge({ ownerId, ownerName, status, currentUserId, onClaim, claiming }) {
  if (ownerId === currentUserId) {
    return <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">Mine</span>;
  }
  if (!ownerId) {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); onClaim(); }}
        disabled={claiming}
        className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-medium hover:bg-yellow-200 transition-colors disabled:opacity-50"
      >
        {claiming ? 'Claiming...' : 'Claim'}
      </button>
    );
  }
  if (ownerId) {
    return <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{ownerName || 'Assigned'}</span>;
  }
  return <span className="text-slate-400">—</span>;
}

// Defined OUTSIDE CompaniesPage to prevent remount on every parent render
function LeadCountCell({ company, leadsCache }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [leads, setLeads] = useState(null);
  const [loadingLeads, setLoadingLeads] = useState(false);

  const fetchLeads = async () => {
    if (leadsCache.current.has(company.id)) {
      setLeads(leadsCache.current.get(company.id));
      return;
    }
    setLoadingLeads(true);
    try {
      const res = await axios.get(`${API}/leads?company_id=${company.id}&limit=50`);
      const data = res.data.items || [];
      leadsCache.current.set(company.id, data);
      setLeads(data);
    } catch {
      setLeads([]);
    } finally {
      setLoadingLeads(false);
    }
  };

  const count = company.lead_count ?? 0;
  if (count === 0) {
    return (
      <Badge className="bg-slate-100 text-slate-500 cursor-default">
        <Users className="h-3 w-3 mr-1" />0
      </Badge>
    );
  }

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) fetchLeads(); }}>
      <PopoverTrigger asChild>
        <button onClick={(e) => e.stopPropagation()}>
          <Badge className="bg-blue-100 text-blue-700 cursor-pointer hover:bg-blue-200 transition-colors">
            <Users className="h-3 w-3 mr-1" />
            {count}
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2 max-h-72 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <p className="text-xs font-semibold text-slate-500 mb-2 px-1">
          Leads at {company.name}
        </p>
        {loadingLeads || leads === null ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          </div>
        ) : leads.length === 0 ? (
          <p className="text-xs text-slate-400 px-1">No leads found</p>
        ) : (
          <div className="space-y-0.5">
            {leads.map((lead) => (
              <button
                key={lead.id}
                onClick={() => navigate(`/admin/leads/${lead.id}`)}
                className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-slate-100 transition-colors"
              >
                <span className="font-medium text-slate-800">
                  {lead.first_name} {lead.last_name || ''}
                </span>
                {lead.title && (
                  <span className="block text-xs text-slate-400 truncate">{lead.title}</span>
                )}
              </button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function CompanyLogo({ company, onUpdate }) {
  const [logoError, setLogoError] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [updatingLogo, setUpdatingLogo] = useState(false);
  const [searching, setSearching] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const fileRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUpdatingLogo(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        await axios.patch(`${API}/companies/${company.id}/logo`, { logo_url: ev.target.result });
        toast.success('Logo updated');
        setOpen(false);
        onUpdate();
      } catch {
        toast.error('Failed to upload logo');
      } finally {
        setUpdatingLogo(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleUrlSubmit = async () => {
    if (!urlInput.trim()) return;
    setUpdatingLogo(true);
    try {
      await axios.patch(`${API}/companies/${company.id}/logo`, { website_url: urlInput.trim() });
      toast.success('Logo updated');
      setOpen(false);
      setUrlInput('');
      onUpdate();
    } catch {
      toast.error('Failed to fetch logo');
    } finally {
      setUpdatingLogo(false);
    }
  };

  const handleAISearch = async () => {
    setSearching(true);
    setSuggestions([]);
    try {
      const res = await axios.post(`${API}/companies/${company.id}/find-logo`);
      setSuggestions(res.data.results || []);
      if (!res.data.results?.length) toast.info('No logos found for this company');
    } catch {
      toast.error('AI logo search failed');
    } finally {
      setSearching(false);
    }
  };

  const pickSuggestion = async (logoUrl) => {
    try {
      await axios.patch(`${API}/companies/${company.id}/logo`, { logo_url: logoUrl });
      toast.success('Logo saved');
      setOpen(false);
      setSuggestions([]);
      onUpdate();
    } catch {
      toast.error('Failed to save logo');
    }
  };

  const showLogo = company.logo_url && !logoError;

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSuggestions([]); }}>
      <PopoverTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          className="w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center bg-indigo-100 hover:ring-2 hover:ring-indigo-400 transition-all shrink-0"
          title="Click to update logo"
        >
          {showLogo ? (
            <img
              src={company.logo_url}
              alt={company.name}
              className="w-full h-full object-contain p-1"
              onError={() => setLogoError(true)}
            />
          ) : (
            <Building2 className="h-5 w-5 text-indigo-600" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" onClick={(e) => e.stopPropagation()}>
        <p className="text-sm font-semibold mb-3">Update Logo — {company.name}</p>
        <div className="space-y-2">
          <Button
            variant="outline" size="sm"
            className="w-full justify-start gap-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            onClick={handleAISearch}
            disabled={searching || updatingLogo}
          >
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {searching ? 'Searching...' : 'AI Auto-Find Logo'}
          </Button>
          {suggestions.length > 0 && (
            <div className="border rounded-md p-2 space-y-1 bg-slate-50">
              <p className="text-xs text-slate-500 mb-1">Select a logo:</p>
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => pickSuggestion(s.logo)}
                  className="flex items-center gap-2 w-full hover:bg-white rounded p-1 transition-colors"
                >
                  <img src={s.logo} alt={s.name} className="w-8 h-8 object-contain rounded border bg-white p-0.5" onError={(e) => e.target.style.display='none'} />
                  <div className="text-left">
                    <p className="text-xs font-medium">{s.name}</p>
                    <p className="text-xs text-slate-400">{s.domain}</p>
                  </div>
                  <CheckCircle className="h-3 w-3 text-green-500 ml-auto" />
                </button>
              ))}
            </div>
          )}
          <div className="border-t pt-2 space-y-2">
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
            <Button variant="outline" size="sm" className="w-full justify-start gap-2" onClick={() => fileRef.current?.click()} disabled={updatingLogo}>
              <Upload className="h-4 w-4" /> Upload Logo
            </Button>
            <div className="flex gap-1">
              <Input
                placeholder="Enter website URL"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleUrlSubmit()}
                className="text-xs h-8"
              />
              <Button size="sm" className="h-8 px-2 shrink-0" onClick={handleUrlSubmit} disabled={updatingLogo || !urlInput.trim()}>
                {updatingLogo ? <Loader2 className="h-3 w-3 animate-spin" /> : <Link className="h-3 w-3" />}
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function CompaniesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [claimingId, setClaimingId] = useState(null);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [formData, setFormData] = useState({ name: '', website: '', num_recruiters: '', num_reqs_open: '', geographies: '', years_in_business: '', revenues: '', locations: [], ats_used: '', ai_voice_using: '', other_ai_tools: '', company_notes: '' });
  const [saving, setSaving] = useState(false);
  const [fetchUrl, setFetchUrl] = useState('');
  const [fetching, setFetching] = useState(false);
  const [fetchMsgIdx, setFetchMsgIdx] = useState(0);
  const fetchTimerRef = useRef(null);
  const fetchCancelRef = useRef(false);
  const previewTimerRef = useRef(null);
  const [webPreview, setWebPreview] = useState(null); // { url, rect }
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateMatches, setDuplicateMatches] = useState([]);
  
  // Selection for bulk actions
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState(() => { try { return JSON.parse(localStorage.getItem('jtcrm_companies_filters') || '{}').searchQuery || ''; } catch { return ''; } });
  const [viewFilter, setViewFilter] = useState(() => { try { return JSON.parse(localStorage.getItem('jtcrm_companies_filters') || '{}').viewFilter || 'mine'; } catch { return 'mine'; } }); // 'all' | 'new' | 'mine'
  const [useComp, setUseComp] = useState(() => { try { return JSON.parse(localStorage.getItem('jtcrm_companies_filters') || '{}').useComp || false; } catch { return false; } });
  const [atsFilter, setAtsFilter] = useState(() => { try { return JSON.parse(localStorage.getItem('jtcrm_companies_filters') || '{}').atsFilter || 'all'; } catch { return 'all'; } });

  // Company data cache
  const cacheRef = useRef({
    companies: null,
    timestamp: null
  });
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // Leads preview cache — Map<company_id, lead[]> — shared across all LeadCountCells
  const leadsCache = useRef(new Map());

  useEffect(() => {
    fetchCompanies(false);
  }, []);

  // Auto-open Add Company dialog when navigated here with openAddCompany state
  useEffect(() => {
    if (location.state?.openAddCompany) {
      setFormData({ name: '', website: '', num_recruiters: '', num_reqs_open: '', geographies: '', years_in_business: '', revenues: '', locations: [], ats_used: '', ai_voice_using: '', other_ai_tools: '', company_notes: '' });
      setCreateDialogOpen(true);
      // Clear the state so re-renders don't reopen it
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  const fetchCompanies = async (forceRefresh = false) => {
    const now = Date.now();
    const cache = cacheRef.current;
    
    // Use cached data if valid and not forcing refresh
    if (!forceRefresh && cache.timestamp && (now - cache.timestamp) < CACHE_DURATION && cache.companies) {
      setCompanies(cache.companies);
      setLoading(false);
      return;
    }
    
    if (forceRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      const response = await axios.get(`${API}/companies?limit=1000`);
      const data = response.data.items || [];
      
      // Update cache
      cacheRef.current = {
        companies: data,
        timestamp: Date.now()
      };
      
      setCompanies(data);
      // Prefetch leads for all companies with leads in the background
      prefetchAllLeads(data);

      if (forceRefresh) {
        toast.success('Companies data refreshed');
      }
    } catch (error) {
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Prefetch leads for all companies with lead_count > 0 — single batch request
  const prefetchAllLeads = async (companiesList) => {
    const idsWithLeads = (companiesList || companies)
      .filter(c => (c.lead_count ?? 0) > 0)
      .map(c => c.id);

    if (idsWithLeads.length === 0) return;

    try {
      const res = await axios.post(`${API}/companies/batch-leads-preview`, {
        company_ids: idsWithLeads
      });
      const grouped = res.data.leads || {};
      Object.entries(grouped).forEach(([companyId, leads]) => {
        leadsCache.current.set(companyId, leads);
      });
      // Companies with no leads in response → set empty array so popover knows
      idsWithLeads.forEach(id => {
        if (!leadsCache.current.has(id)) leadsCache.current.set(id, []);
      });
    } catch {
      // Silently ignore — LeadCountCell will fetch on demand as fallback
    }
  };
  
  // Reset filters and show cached data
  const handleReset = () => {
    setSearchQuery('');
    setUseComp(false);
    setAtsFilter('all');
    setSelectedIds(new Set());
    localStorage.removeItem('jtcrm_companies_filters');
    toast.success('Filters cleared');
  };

  useEffect(() => {
    localStorage.setItem('jtcrm_companies_filters', JSON.stringify({ searchQuery, viewFilter, useComp, atsFilter }));
  }, [searchQuery, viewFilter, useComp, atsFilter]);
  
  // Force refresh from database
  const handleRefresh = () => {
    fetchCompanies(true);
  };

  // Column definitions for AdvancedDataGrid
  const columns = [
    {
      key: 'name',
      label: 'Company Name',
      sortable: true,
      filterable: true,
      required: true,
      render: (row) => (
        <div className="flex items-center gap-3">
          <CompanyLogo company={row} onUpdate={() => fetchCompanies(true)} />
          <div className="min-w-0">
            <div className="flex items-center gap-1 group">
              <RouterLink
                to={`/admin/companies/${row.id}`}
                className="font-medium text-slate-900 hover:text-indigo-600 hover:underline truncate"
                onClick={e => e.stopPropagation()}
              >
                {row.name}
              </RouterLink>
              <a
                href={`/admin/companies/${row.id}`}
                target="_blank"
                rel="noopener noreferrer"
                title="Open in new tab"
                className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-indigo-600 transition-opacity flex-shrink-0"
                onClick={e => e.stopPropagation()}
              >
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
            {row.website && (
              <span
                className="flex items-center gap-1 text-xs text-blue-500 hover:underline cursor-pointer"
                onClick={(e) => { e.stopPropagation(); window.open(row.website.startsWith('http') ? row.website : `https://${row.website}`, '_blank'); }}
                onMouseEnter={(e) => {
                  e.stopPropagation();
                  clearTimeout(previewTimerRef.current);
                  const rect = e.currentTarget.getBoundingClientRect();
                  previewTimerRef.current = setTimeout(() => {
                    const url = row.website.startsWith('http') ? row.website : `https://${row.website}`;
                    setWebPreview({ url, rect });
                  }, 500);
                }}
                onMouseLeave={() => {
                  clearTimeout(previewTimerRef.current);
                  previewTimerRef.current = setTimeout(() => setWebPreview(null), 300);
                }}
              >
                <Globe className="h-3 w-3 flex-shrink-0" />
                <span className="truncate max-w-[160px]">{row.website.replace(/^https?:\/\//, '')}</span>
              </span>
            )}
            <div className="text-xs text-slate-400">{row.locations?.length || 0} location(s)</div>
          </div>
        </div>
      )
    },
    {
      key: 'locations_count',
      label: 'Locations',
      sortable: true,
      render: (row) => (
        <Badge variant="outline" className="bg-slate-50">
          <MapPin className="h-3 w-3 mr-1" />
          {row.locations?.length || 0}
        </Badge>
      )
    },
    {
      key: 'primary_country',
      label: 'Country',
      sortable: true,
      filterable: true,
      filterType: 'status',
      filterOptions: COUNTRIES.map(c => ({ value: c, label: c })),
      getValue: (row) => row.locations?.[0]?.country || '',
      render: (row) => {
        const primaryLocation = row.locations?.[0];
        return primaryLocation?.country ? (
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-slate-400" />
            <span>{primaryLocation.country}</span>
          </div>
        ) : <span className="text-slate-400">—</span>;
      }
    },
    {
      key: 'primary_city',
      label: 'City',
      sortable: true,
      filterable: true,
      getValue: (row) => row.locations?.[0]?.city || '',
      render: (row) => {
        const primaryLocation = row.locations?.[0];
        return primaryLocation?.city || <span className="text-slate-400">—</span>;
      }
    },
    {
      key: 'leads_count',
      label: 'Leads',
      sortable: true,
      sortType: 'number',
      render: (row) => <LeadCountCell company={row} leadsCache={leadsCache} />
    },
    {
      key: 'last_activity_at',
      label: 'Last Activity',
      sortable: true,
      sortType: 'date',
      render: (row) => {
        if (!row.last_activity_at) return <span className="text-slate-300">No activity</span>;
        try {
          return (
            <span className="text-slate-600 text-xs" title={format(parseISO(row.last_activity_at), 'MMM d, yyyy h:mm a')}>
              {formatDistanceToNow(parseISO(row.last_activity_at), { addSuffix: true })}
            </span>
          );
        } catch { return '—'; }
      }
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      sortType: 'date',
      render: (row) => row.created_at ? format(parseISO(row.created_at), 'MMM d, yyyy') : '—'
    },
    {
      key: 'owner_name',
      label: 'Owner',
      sortable: true,
      render: (row) => (
        <OwnerBadge
          ownerId={row.owner_id}
          ownerName={row.owner_name}
          status={row.status}
          currentUserId={currentUser?.id}
          onClaim={() => claimCompany(row.id)}
          claiming={claimingId === row.id}
        />
      ),
    },
    {
      key: 'research_status',
      label: 'Intelligence',
      sortable: true,
      render: (row) => {
        if (row.research_status === 'complete') {
          return (
            <div className="flex flex-col gap-1">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                ✓ Intel
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                ✓ DMs
              </span>
            </div>
          );
        }
        if (row.research_status === 'in_progress') {
          return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">⟳ Running</span>;
        }
        if (row.research_status === 'queued') {
          return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">⏳ Queued</span>;
        }
        return <span className="text-slate-300 text-xs">—</span>;
      },
    }
  ];

  const FETCH_MESSAGES = [
    '🔍 Googling your company... shh, don\'t tell Google',
    '🤖 Asking our AI overlords nicely for intel...',
    '🕵️ Professionally stalking their digital presence...',
    '☕ Brewing some fresh company intelligence...',
    '📡 Beaming data from the cloud dimension...',
    '🧠 Teaching Claude what staffing means (again)...',
    '🏢 Knocking on their digital front door...',
    '🔮 Consulting the crystal ball of business intel...',
    '📊 Crunching numbers we haven\'t found yet...',
    '🦾 Flexing our research muscles...',
  ];

  const cleanFormData = (data) => ({
    ...data,
    num_recruiters: data.num_recruiters === '' ? null : data.num_recruiters,
    num_reqs_open: data.num_reqs_open === '' ? null : data.num_reqs_open,
    years_in_business: data.years_in_business === '' ? null : data.years_in_business,
  });

  const cancelFetch = () => {
    fetchCancelRef.current = true;
    clearInterval(fetchTimerRef.current);
    setFetching(false);
    setFetchMsgIdx(0);
  };

  const normalizeDomain = (url) => {
    try {
      let u = url.trim();
      if (!u.startsWith('http')) u = 'https://' + u;
      return new URL(u).hostname.replace(/^www\./, '').toLowerCase();
    } catch {
      return url.trim().replace(/^www\./, '').toLowerCase();
    }
  };

  const fetchFromUrl = async () => {
    if (!fetchUrl.trim()) return;

    // Check if this URL already exists in the company list
    const inputDomain = normalizeDomain(fetchUrl);
    const urlMatch = companies.find(c => c.website && normalizeDomain(c.website) === inputDomain);
    if (urlMatch) {
      setDuplicateMatches([urlMatch]);
      setDuplicateDialogOpen(true);
      return;
    }

    fetchCancelRef.current = false;
    setFetching(true);
    setFetchMsgIdx(0);
    fetchTimerRef.current = setInterval(() => {
      setFetchMsgIdx(i => (i + 1) % FETCH_MESSAGES.length);
    }, 2500);
    try {
      const res = await axios.post(`${API}/companies/prefill-from-url`, { url: fetchUrl.trim() });
      if (fetchCancelRef.current) return;
      const d = res.data;
      setFormData(prev => ({
        ...prev,
        name:             d.name         || prev.name,
        website:          d.website      || prev.website,
        geographies:      d.geographies  || prev.geographies,
        revenues:         d.revenue_estimate || prev.revenues,
        years_in_business: d.founded_year
          ? String(new Date().getFullYear() - d.founded_year)
          : prev.years_in_business,
        company_notes: d.description
          ? (prev.company_notes ? prev.company_notes + '\n\n' + d.description : d.description)
          : prev.company_notes,
      }));
      toast.success('Company details fetched — review and adjust as needed');
    } catch (e) {
      if (!fetchCancelRef.current) {
        toast.error(e.response?.data?.detail || 'Could not fetch company data');
      }
    } finally {
      clearInterval(fetchTimerRef.current);
      setFetching(false);
      setFetchMsgIdx(0);
    }
  };

  const resetCreateForm = () => {
    setFormData({ name: '', website: '', num_recruiters: '', num_reqs_open: '', geographies: '', years_in_business: '', revenues: '', locations: [], ats_used: '', ai_voice_using: '', other_ai_tools: '', company_notes: '' });
    setFetchUrl('');
    setCreateDialogOpen(false);
    setDuplicateDialogOpen(false);
  };

  const doCreate = async () => {
    setSaving(true);
    const urlToResearch = fetchUrl.trim();
    try {
      const res = await axios.post(`${API}/companies`, cleanFormData(formData));
      const newCompany = res.data;
      toast.success('Company created');
      resetCreateForm();
      fetchCompanies(true);
      if (urlToResearch && newCompany?.id) {
        toast.info('Starting full intelligence research in background...');
        axios.post(`${API}/companies/${newCompany.id}/research`).catch(() => {});
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create company');
    } finally {
      setSaving(false);
    }
  };

  const doUpdateExisting = async (existing) => {
    setSaving(true);
    try {
      // Merge: bring over non-empty form fields the existing record lacks
      const merged = { ...existing };
      const fields = ['website', 'geographies', 'revenues', 'years_in_business',
                      'ats_used', 'ai_voice_using', 'other_ai_tools', 'num_recruiters', 'num_reqs_open'];
      fields.forEach(f => {
        if (formData[f] !== '' && formData[f] != null && !existing[f]) merged[f] = formData[f];
      });
      if (formData.company_notes && !existing.company_notes) merged.company_notes = formData.company_notes;
      if (formData.locations?.length && !existing.locations?.length) merged.locations = formData.locations;

      await axios.put(`${API}/companies/${existing.id}`, merged);
      toast.success(`Updated "${existing.name}" — starting intelligence research...`);
      resetCreateForm();
      fetchCompanies(true);
      axios.post(`${API}/companies/${existing.id}/research`).catch(() => {});
      navigate(`/admin/companies/${existing.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update company');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    const name = formData.name.trim().toLowerCase();
    const allMatches = companies.filter(c =>
      c.name?.toLowerCase().includes(name) || name.includes(c.name?.toLowerCase())
    );
    if (allMatches.length > 0) {
      // Prefer exact match, then first partial
      const exact = allMatches.find(c => c.name?.toLowerCase() === name);
      setDuplicateMatches([exact || allMatches[0]]);
      setDuplicateDialogOpen(true);
      return;
    }
    await doCreate();
  };

  const handleUpdate = async () => {
    if (!formData.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/companies/${selectedCompany.id}`, cleanFormData(formData));
      toast.success('Company updated');
      setEditDialogOpen(false);
      fetchCompanies(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update company');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/companies/${selectedCompany.id}`);
      toast.success('Company deleted');
      setDeleteDialogOpen(false);
      fetchCompanies(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete company');
    }
  };

  const claimCompany = async (companyId) => {
    setClaimingId(companyId);
    try {
      await axios.put(`${API}/companies/${companyId}/claim`);
      toast.success('Company claimed — you are now the owner');
      fetchCompanies(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to claim company');
    } finally {
      setClaimingId(null);
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(Array.from(selectedIds).map(id => axios.delete(`${API}/companies/${id}`)));
      toast.success(`${selectedIds.size} companies deleted`);
      setBulkDeleteDialogOpen(false);
      setSelectedIds(new Set());
      fetchCompanies(true);
    } catch (error) {
      toast.error('Failed to delete some companies');
    }
  };

  const openEditDialog = (company) => {
    setSelectedCompany(company);
    setFormData({
      name: company.name,
      website: company.website || '',
      num_recruiters: company.num_recruiters ?? '',
      num_reqs_open: company.num_reqs_open ?? '',
      geographies: company.geographies || '',
      years_in_business: company.years_in_business ?? '',
      revenues: company.revenues || '',
      locations: company.locations || [],
      ats_used: company.ats_used || '',
      ai_voice_using: company.ai_voice_using || '',
      other_ai_tools: company.other_ai_tools || '',
      company_notes: company.company_notes || ''
    });
    setEditDialogOpen(true);
  };

  const addLocation = () => {
    setFormData({
      ...formData,
      locations: [...formData.locations, { 
        id: crypto.randomUUID(),
        name: '', address: '', city: '', state: '', country: 'USA', phone: ''
      }]
    });
  };

  const updateLocation = (index, field, value) => {
    const newLocations = [...formData.locations];
    newLocations[index] = { ...newLocations[index], [field]: value };
    setFormData({ ...formData, locations: newLocations });
  };

  const removeLocation = (index) => {
    setFormData({ ...formData, locations: formData.locations.filter((_, i) => i !== index) });
  };

  // Render row actions
  const renderRowActions = (row) => (
    <div className="flex items-center gap-1">
      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openEditDialog(row); }} data-testid={`edit-company-${row.id}`}>
        <Edit className="h-4 w-4" />
      </Button>
      {row.can_delete !== false && (
        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50"
          onClick={(e) => { e.stopPropagation(); setSelectedCompany(row); setDeleteDialogOpen(true); }}
          data-testid={`delete-company-${row.id}`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );

  // Filter and sort companies — activity-sorted (most recent first), nulls last
  const filteredCompanies = useMemo(() => {
    let list = companies;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      list = companies.filter(company =>
        (company.name || '').toLowerCase().includes(query) ||
        company.locations?.some(loc =>
          (loc.city || '').toLowerCase().includes(query) ||
          (loc.country || '').toLowerCase().includes(query) ||
          (loc.name || '').toLowerCase().includes(query)
        )
      );
    }
    // View filter
    if (viewFilter === 'new') {
      list = list.filter(c => !c.owner_id);
    } else if (viewFilter === 'mine') {
      list = list.filter(c => c.owner_id === currentUser?.id);
    }
    // Competitor product usage filter — show only companies using non-JobTalk AI voice products
    if (useComp) {
      list = list.filter(c => c.ai_voice_using && c.ai_voice_using.toLowerCase() !== 'jobtalk');
    }
    // ATS filter
    if (atsFilter !== 'all') {
      list = list.filter(c => c.ats_used && c.ats_used.toLowerCase() === atsFilter.toLowerCase());
    }
    // Sort: last_activity_at DESC (nulls last), then created_at DESC
    return [...list].sort((a, b) => {
      const aTime = a.last_activity_at ? new Date(a.last_activity_at).getTime() : 0;
      const bTime = b.last_activity_at ? new Date(b.last_activity_at).getTime() : 0;
      if (bTime !== aTime) return bTime - aTime;
      return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
    });
  }, [companies, searchQuery, viewFilter, useComp, atsFilter, currentUser]);

  const handleExport = () => {
    const rows = filteredCompanies;
    if (!rows.length) { toast.error('No companies to export'); return; }

    const headers = [
      'Name', 'Website', 'Company Type', 'Industry', 'ATS Used',
      'Num Recruiters', 'Open Reqs', 'Years in Business', 'Geographies',
      'Revenues', 'Company Notes',
      'Address', 'City', 'State', 'ZIP',
      'Locations', 'Lead Count', 'Created At'
    ];

    const escape = (v) => {
      if (v == null) return '';
      const s = String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const csvRows = rows.map(c => [
      escape(c.name),
      escape(c.website || c.url),
      escape(c.company_type),
      escape(c.industry),
      escape(c.ats_used),
      escape(c.num_recruiters),
      escape(c.num_reqs_open),
      escape(c.years_in_business),
      escape(c.geographies),
      escape(c.revenues),
      escape(c.company_notes),
      escape(c.address1),
      escape(c.city),
      escape(c.state),
      escape(c.zip),
      escape((c.locations || []).map(l => l.name).join('; ')),
      escape(c.lead_count ?? ''),
      escape(c.created_at ? c.created_at.slice(0, 10) : ''),
    ].join(','));

    const csv = [headers.join(','), ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `companies_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} companies`);
  };

  // Batch logo state
  const [batchFinding, setBatchFinding] = useState(false);

  const handleBatchFindLogos = async () => {
    setBatchFinding(true);
    try {
      const res = await axios.post(`${API}/companies/batch-find-logos`);
      const { updated, total } = res.data;
      toast.success(`Found logos for ${updated} of ${total} companies`);
      fetchCompanies(true);
    } catch {
      toast.error('Failed to auto-find logos');
    } finally {
      setBatchFinding(false);
    }
  };



  return (
    <div className="space-y-6 animate-fade-in w-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            CRM Companies
          </h1>
          <p className="text-slate-600 mt-1">Manage companies and their locations</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setCsvImportOpen(true)}>
            <Upload className="h-4 w-4 mr-2" />Import
          </Button>
          <Button variant="outline" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />Export
          </Button>
          <Button variant="outline" onClick={handleBatchFindLogos} disabled={batchFinding} title="Auto-find logos for all companies using AI">
            {batchFinding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            {batchFinding ? 'Finding Logos...' : 'Auto-find Logos'}
          </Button>
          <Button onClick={() => { setFormData({ name: '', website: '', num_recruiters: '', num_reqs_open: '', geographies: '', years_in_business: '', revenues: '', locations: [], ats_used: '', ai_voice_using: '', other_ai_tools: '', company_notes: '' }); setCreateDialogOpen(true); }} data-testid="add-company-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Company
          </Button>
        </div>
      </div>

      {/* Search Box with Reset and Refresh */}
      <div className="flex items-center gap-2 max-w-2xl">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input
            placeholder="Search by company name, city, or country..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10 h-11 bg-white border-slate-200 focus:border-indigo-500"
            data-testid="companies-search"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={handleReset}
          title="Reset filters (use cached data)"
          className="h-11 w-11 shrink-0"
          data-testid="reset-companies-btn"
        >
          <RotateCcw className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={handleRefresh}
          disabled={refreshing}
          title="Refresh data from database"
          className="h-11 w-11 shrink-0"
          data-testid="refresh-companies-btn"
        >
          {refreshing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
        </Button>
      </div>
      {searchQuery && (
        <p className="text-sm text-slate-500">
          Found {filteredCompanies.length} {filteredCompanies.length === 1 ? 'company' : 'companies'} matching &quot;{searchQuery}&quot;
        </p>
      )}

      {/* View filter toggle */}
      <div className="flex items-center gap-1">
        {[
          { value: 'all', label: 'All Companies', count: companies.length },
          { value: 'new', label: 'New', count: companies.filter(c => !c.owner_id).length },
          { value: 'mine', label: 'My Companies', count: currentUser?.id ? companies.filter(c => c.owner_id === currentUser.id).length : 0 },
        ].map(tab => (
          <button
            key={tab.value}
            onClick={() => {
              setViewFilter(tab.value);
              try { const s = JSON.parse(localStorage.getItem('jtcrm_companies_filters') || '{}'); localStorage.setItem('jtcrm_companies_filters', JSON.stringify({ ...s, viewFilter: tab.value })); } catch {}
            }}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              viewFilter === tab.value
                ? 'bg-indigo-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {tab.label}
            <span className={`text-xs px-1.5 py-0.5 rounded-full ${
              viewFilter === tab.value ? 'bg-indigo-500 text-white' : 'bg-slate-200 text-slate-500'
            }`}>
              {tab.count}
            </span>
          </button>
        ))}

        {/* Competitor product usage filter */}
        <label className="inline-flex items-center gap-2 ml-4 px-3 py-1.5 rounded-full text-sm font-medium cursor-pointer select-none bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors">
          <Checkbox
            checked={useComp}
            onCheckedChange={(checked) => setUseComp(!!checked)}
            className="h-4 w-4"
          />
          Use Comp
          {useComp && (
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-orange-500 text-white">
              {companies.filter(c => c.ai_voice_using && c.ai_voice_using.toLowerCase() !== 'jobtalk').length}
            </span>
          )}
        </label>

        {/* ATS filter dropdown */}
        <select
          value={atsFilter}
          onChange={(e) => setAtsFilter(e.target.value)}
          className={`ml-2 px-3 py-1.5 rounded-full text-sm font-medium border-0 cursor-pointer transition-colors appearance-none pr-7 ${
            atsFilter !== 'all'
              ? 'bg-indigo-600 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
          style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='${atsFilter !== 'all' ? 'white' : '%2364748b'}' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center' }}
        >
          <option value="all">ATS: All</option>
          <option value="JobDiva">JobDiva</option>
          <option value="BullHorn">BullHorn</option>
          <option value="CeiPal">CeiPal</option>
        </select>
      </div>

      {/* Advanced Data Grid */}
      <AdvancedDataGrid
        columns={columns}
        data={filteredCompanies}
        defaultSort={{ key: 'last_activity_at', direction: 'desc' }}
        loading={loading}
        onRowClick={(row) => navigate(`/admin/companies/${row.id}`)}
        renderRowActions={renderRowActions}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        onBulkDelete={() => setBulkDeleteDialogOpen(true)}
        rowIdKey="id"
        emptyMessage={searchQuery ? "No companies match your search" : "No companies found. Add your first company!"}
        emptyIcon={<Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />}
        storageKey="jtcrm_companies_col_filters"
      />

      {/* Create Company Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={(open) => { setCreateDialogOpen(open); if (!open) setFetchUrl(''); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add New Company</DialogTitle></DialogHeader>
          <div className="space-y-6 py-4">
            <p className="text-xs text-slate-500 -mt-2">Fields marked with <span className="text-red-500 font-semibold">*</span> are required. All other fields are optional.</p>

            {/* URL Auto-fill */}
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-2">
              <Label className="text-blue-800 font-semibold flex items-center gap-1.5">
                <Sparkles className="h-4 w-4" /> Auto-fill from Website
              </Label>
              <div className="flex gap-2">
                <Input
                  value={fetchUrl}
                  onChange={(e) => setFetchUrl(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !fetching && fetchFromUrl()}
                  placeholder="e.g., acme.com or https://acme.com"
                  className="bg-white"
                  disabled={fetching}
                />
                <Button
                  type="button"
                  onClick={fetchFromUrl}
                  disabled={fetching || !fetchUrl.trim()}
                  className="shrink-0 gap-1.5 bg-blue-600 hover:bg-blue-700"
                >
                  {fetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {fetching ? 'Fetching...' : 'Fetch'}
                </Button>
              </div>
              {fetching ? (
                <div className="flex items-center justify-between gap-2 rounded-md bg-blue-100 px-3 py-2">
                  <span className="text-xs text-blue-800 font-medium">{FETCH_MESSAGES[fetchMsgIdx]}</span>
                  <button
                    type="button"
                    onClick={cancelFetch}
                    className="text-xs text-red-600 hover:text-red-700 font-medium whitespace-nowrap shrink-0"
                  >
                    ✕ Cancel
                  </button>
                </div>
              ) : (
                <p className="text-xs text-blue-600">Enter a domain and click Fetch to auto-fill the form. Saving will also start full research.</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Company Name <span className="text-red-500">*</span></Label>
              <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="e.g., Acme Corporation" data-testid="company-name" />
            </div>

            <div className="space-y-2">
              <Label>Website</Label>
              <Input value={formData.website} onChange={(e) => setFormData({...formData, website: e.target.value})} placeholder="e.g., https://acme.com" data-testid="company-website" />
            </div>

            {/* Tech Stack Section */}
            <div className="border-t pt-4">
              <h3 className="font-medium text-slate-900 mb-4">Tech Stack</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>ATS Used</Label>
                  <Input
                    list="ats-options"
                    value={formData.ats_used}
                    onChange={(e) => setFormData({...formData, ats_used: e.target.value})}
                    placeholder="e.g., JobDiva"
                    data-testid="ats-used"
                  />
                  <datalist id="ats-options">
                    {ATS_OPTIONS.map(opt => <option key={opt} value={opt} />)}
                  </datalist>
                </div>
                <div className="space-y-2">
                  <Label>AI Voice Using</Label>
                  <Input
                    list="ai-voice-options"
                    value={formData.ai_voice_using}
                    onChange={(e) => setFormData({...formData, ai_voice_using: e.target.value})}
                    placeholder="e.g., JobTalk"
                    data-testid="ai-voice-using"
                  />
                  <datalist id="ai-voice-options">
                    {AI_VOICE_OPTIONS.map(opt => <option key={opt} value={opt} />)}
                  </datalist>
                </div>
                <div className="space-y-2">
                  <Label>Other AI Tools</Label>
                  <Input
                    value={formData.other_ai_tools}
                    onChange={(e) => setFormData({...formData, other_ai_tools: e.target.value})}
                    placeholder="e.g., ChatGPT, Copilot"
                    data-testid="other-ai-tools"
                  />
                </div>
              </div>
            </div>

            {/* Company Details Section */}
            <div className="border-t pt-4">
              <h3 className="font-medium text-slate-900 mb-4">Company Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label># of Recruiters</Label>
                  <Input type="number" min="0" value={formData.num_recruiters} onChange={(e) => setFormData({...formData, num_recruiters: e.target.value})} placeholder="e.g., 25" />
                </div>
                <div className="space-y-2">
                  <Label>Number of Reqs Open</Label>
                  <Input type="number" min="0" value={formData.num_reqs_open} onChange={(e) => setFormData({...formData, num_reqs_open: e.target.value})} placeholder="e.g., 100" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Geographies</Label>
                  <Input value={formData.geographies} onChange={(e) => setFormData({...formData, geographies: e.target.value})} placeholder="e.g., USA, Canada, UK" />
                </div>
                <div className="space-y-2">
                  <Label>Years in Business</Label>
                  <Input type="number" min="0" value={formData.years_in_business} onChange={(e) => setFormData({...formData, years_in_business: e.target.value})} placeholder="e.g., 15" />
                </div>
                <div className="space-y-2">
                  <Label>Revenues</Label>
                  <Input value={formData.revenues} onChange={(e) => setFormData({...formData, revenues: e.target.value})} placeholder="e.g., $50M" />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="border-t pt-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5"><FileText className="h-4 w-4 text-amber-500" /> Notes</Label>
                <Textarea
                  value={formData.company_notes}
                  onChange={(e) => setFormData({...formData, company_notes: e.target.value})}
                  placeholder="Add notes about this company..."
                  className="min-h-[80px] resize-none text-sm"
                />
              </div>
            </div>

            <LocationForm locations={formData.locations} onAdd={addLocation} onUpdate={updateLocation} onRemove={removeLocation} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="create-company-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create Company
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Company Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Company</DialogTitle></DialogHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label>Company Name *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
            </div>

            <div className="space-y-2">
              <Label>Website</Label>
              <Input value={formData.website} onChange={(e) => setFormData({...formData, website: e.target.value})} placeholder="e.g., https://acme.com" />
            </div>

            {/* Tech Stack Section */}
            <div className="border-t pt-4">
              <h3 className="font-medium text-slate-900 mb-4">Tech Stack</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>ATS Used</Label>
                  <Input
                    list="ats-options-edit"
                    value={formData.ats_used}
                    onChange={(e) => setFormData({...formData, ats_used: e.target.value})}
                    placeholder="e.g., JobDiva"
                  />
                  <datalist id="ats-options-edit">
                    {ATS_OPTIONS.map(opt => <option key={opt} value={opt} />)}
                  </datalist>
                </div>
                <div className="space-y-2">
                  <Label>AI Voice Using</Label>
                  <Input
                    list="ai-voice-options-edit"
                    value={formData.ai_voice_using}
                    onChange={(e) => setFormData({...formData, ai_voice_using: e.target.value})}
                    placeholder="e.g., JobTalk"
                  />
                  <datalist id="ai-voice-options-edit">
                    {AI_VOICE_OPTIONS.map(opt => <option key={opt} value={opt} />)}
                  </datalist>
                </div>
                <div className="space-y-2">
                  <Label>Other AI Tools</Label>
                  <Input
                    value={formData.other_ai_tools}
                    onChange={(e) => setFormData({...formData, other_ai_tools: e.target.value})}
                    placeholder="e.g., ChatGPT, Copilot"
                  />
                </div>
              </div>
            </div>

            {/* Company Details Section */}
            <div className="border-t pt-4">
              <h3 className="font-medium text-slate-900 mb-4">Company Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label># of Recruiters</Label>
                  <Input type="number" min="0" value={formData.num_recruiters} onChange={(e) => setFormData({...formData, num_recruiters: e.target.value})} placeholder="e.g., 25" />
                </div>
                <div className="space-y-2">
                  <Label>Number of Reqs Open</Label>
                  <Input type="number" min="0" value={formData.num_reqs_open} onChange={(e) => setFormData({...formData, num_reqs_open: e.target.value})} placeholder="e.g., 100" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Geographies</Label>
                  <Input value={formData.geographies} onChange={(e) => setFormData({...formData, geographies: e.target.value})} placeholder="e.g., USA, Canada, UK" />
                </div>
                <div className="space-y-2">
                  <Label>Years in Business</Label>
                  <Input type="number" min="0" value={formData.years_in_business} onChange={(e) => setFormData({...formData, years_in_business: e.target.value})} placeholder="e.g., 15" />
                </div>
                <div className="space-y-2">
                  <Label>Revenues</Label>
                  <Input value={formData.revenues} onChange={(e) => setFormData({...formData, revenues: e.target.value})} placeholder="e.g., $50M" />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="border-t pt-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5"><FileText className="h-4 w-4 text-amber-500" /> Notes</Label>
                <Textarea
                  value={formData.company_notes}
                  onChange={(e) => setFormData({...formData, company_notes: e.target.value})}
                  placeholder="Add notes about this company..."
                  className="min-h-[80px] resize-none text-sm"
                />
              </div>
            </div>

            <LocationForm locations={formData.locations} onAdd={addLocation} onUpdate={updateLocation} onRemove={removeLocation} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdate} disabled={saving} data-testid="update-company-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Update Company
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Company</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{selectedCompany?.name}&quot;? This will also remove all associated locations. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Duplicate Company Warning */}
      <AlertDialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <AlertDialogContent className="max-w-lg">
          {duplicateMatches.map((match, idx) => {
            const hasIntel = match.research_status === 'complete';
            return (
              <div key={match.id}>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    {hasIntel
                      ? <><span className="text-green-600">✓</span> Company Already Exists with Full Intelligence</>
                      : <><span className="text-amber-500">⚠</span> Similar Company Found</>}
                  </AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <div className="space-y-3 text-sm text-slate-700">
                      {/* Existing company info */}
                      <div className="rounded-lg border bg-slate-50 p-3 space-y-1">
                        <p className="font-semibold text-slate-900 flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-slate-400 shrink-0" />
                          {match.name}
                        </p>
                        {match.website && (
                          <p className="text-xs text-blue-600 pl-6">{match.website}</p>
                        )}
                        <div className="flex gap-2 flex-wrap pl-6 pt-1">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            hasIntel ? 'bg-green-100 text-green-800' : 'bg-slate-200 text-slate-600'
                          }`}>
                            {hasIntel ? '✓ Intelligence' : '✗ No Intelligence'}
                          </span>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            hasIntel ? 'bg-green-100 text-green-800' : 'bg-slate-200 text-slate-600'
                          }`}>
                            {hasIntel ? '✓ Decision Makers' : '✗ No Decision Makers'}
                          </span>
                        </div>
                      </div>

                      {hasIntel ? (
                        <p className="text-slate-600">
                          This company is already fully researched. No need to create a duplicate — all intelligence and decision makers are on file.
                        </p>
                      ) : (
                        <p className="text-slate-600">
                          This company exists but has no intelligence data yet. Click <strong>Update &amp; Research</strong> to merge your data into the existing record and run full research — no duplicate will be created.
                        </p>
                      )}
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="mt-4">
                  <AlertDialogCancel onClick={resetCreateForm}>Cancel</AlertDialogCancel>
                  {hasIntel ? (
                    <AlertDialogAction
                      onClick={() => { setDuplicateDialogOpen(false); navigate(`/admin/companies/${match.id}`); }}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      View Company
                    </AlertDialogAction>
                  ) : (
                    <AlertDialogAction
                      onClick={() => doUpdateExisting(match)}
                      disabled={saving}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Update &amp; Research Existing
                    </AlertDialogAction>
                  )}
                </AlertDialogFooter>
              </div>
            );
          })}
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} Companies</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedIds.size} selected companies? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} className="bg-red-600 hover:bg-red-700">
              Delete {selectedIds.size} Companies
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* CSV/XLSX Import Wizard (companies only) */}
      <CSVImportWizard
        open={csvImportOpen}
        onOpenChange={setCsvImportOpen}
        onImportComplete={() => { setCsvImportOpen(false); fetchCompanies(true); }}
        companyOnly={true}
      />

      {/* Website hover preview popup */}
      {webPreview && (() => {
        const POPUP_W = 680;
        const POPUP_H = 420;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const left = Math.min(webPreview.rect.left, vw - POPUP_W - 12);
        const top = webPreview.rect.bottom + 6 + POPUP_H > vh
          ? webPreview.rect.top - POPUP_H - 6
          : webPreview.rect.bottom + 6;
        return (
          <div
            style={{ position: 'fixed', left, top, width: POPUP_W, height: POPUP_H, zIndex: 9999 }}
            onMouseEnter={() => clearTimeout(previewTimerRef.current)}
            onMouseLeave={() => { clearTimeout(previewTimerRef.current); previewTimerRef.current = setTimeout(() => setWebPreview(null), 200); }}
            className="rounded-xl shadow-2xl border border-slate-200 overflow-hidden bg-white flex flex-col"
          >
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 border-b border-slate-200 shrink-0">
              <Globe className="h-3.5 w-3.5 text-slate-400" />
              <span className="text-xs text-slate-500 truncate flex-1">{webPreview.url}</span>
              <a href={webPreview.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-slate-400 hover:text-indigo-600">
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
              <button onClick={() => setWebPreview(null)} className="text-slate-400 hover:text-slate-600">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <iframe
              src={webPreview.url}
              title="Website preview"
              className="flex-1 w-full"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
            />
          </div>
        );
      })()}
    </div>
  );
}
